import React, { useState, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { paceBand } from '@/lib/plain-language'

// NOTE: This widget is purely illustrative — does NOT display real rate data

const BALANCED_CURVE: [number, number][] = [
  [0.0, -20], [0.3, -14], [0.5, -9], [0.7, -5], [1.0, 0],
  [1.3, 7], [1.5, 12], [2.0, 22], [3.0, 35], [4.0, 45],
]

const BASE_RATE = 100
const NORMAL_BASELINE = 60

/** Linear interpolation between curve points */
function interpolate(points: [number, number][], x: number): number {
  if (x <= points[0][0]) return points[0][1]
  if (x >= points[points.length - 1][0]) return points[points.length - 1][1]

  for (let i = 0; i < points.length - 1; i++) {
    const [x0, y0] = points[i]
    const [x1, y1] = points[i + 1]
    if (x >= x0 && x <= x1) {
      const t = (x - x0) / (x1 - x0)
      return y0 + t * (y1 - y0)
    }
  }
  return 0
}

export default function EventDemandScenario() {
  const [multiplier, setMultiplier] = useState(1.5)
  const [actualOccupancy, setActualOccupancy] = useState(75)

  const computed = useMemo(() => {
    const adjustedBaseline = Math.min(NORMAL_BASELINE * multiplier, 95)
    const paceRatio = Math.min(actualOccupancy / adjustedBaseline, 4.0)
    const demandCorrectionPct = interpolate(BALANCED_CURVE, paceRatio)
    const eventAdjustmentPct = (multiplier - 1) * 100
    const finalRate = Math.round(BASE_RATE * (1 + demandCorrectionPct / 100) * multiplier)
    return { adjustedBaseline, paceRatio, demandCorrectionPct, eventAdjustmentPct, finalRate }
  }, [multiplier, actualOccupancy])

  const {
    adjustedBaseline,
    paceRatio,
    demandCorrectionPct,
    eventAdjustmentPct,
    finalRate,
  } = computed

  return (
    <div className="space-y-5">
      {/* Sliders */}
      <div className="space-y-3">
        {/* Event multiplier slider */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-xs text-muted-foreground font-medium">
              Event multiplier
            </label>
            <span className="text-xs font-semibold tabular-nums text-foreground">
              {multiplier.toFixed(2)}×
            </span>
          </div>
          <input
            type="range"
            min={1.0}
            max={2.5}
            step={0.05}
            value={multiplier}
            onChange={(e) => setMultiplier(Number(e.target.value))}
            className="w-full h-2 rounded-full appearance-none cursor-pointer bg-slate-200 accent-primary
                       [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4
                       [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:shadow-md
                       [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110"
          />
          <div className="flex justify-between text-[10px] text-muted-foreground tabular-nums">
            <span>1.0×</span>
            <span>1.5×</span>
            <span>2.0×</span>
            <span>2.5×</span>
          </div>
        </div>

        {/* Actual occupancy slider */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-xs text-muted-foreground font-medium">
              Actual occupancy
            </label>
            <span className="text-xs font-semibold tabular-nums text-foreground">
              {actualOccupancy}%
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={actualOccupancy}
            onChange={(e) => setActualOccupancy(Number(e.target.value))}
            className="w-full h-2 rounded-full appearance-none cursor-pointer bg-slate-200 accent-primary
                       [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4
                       [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:shadow-md
                       [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110"
          />
          <div className="flex justify-between text-[10px] text-muted-foreground tabular-nums">
            <span>0%</span>
            <span>25%</span>
            <span>50%</span>
            <span>75%</span>
            <span>100%</span>
          </div>
        </div>
      </div>

      {/* Results panel */}
      <div className="rounded-lg border border-border/40 bg-slate-50 p-3 space-y-2">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
          <span className="text-muted-foreground">Adjusted baseline</span>
          <span className="font-semibold tabular-nums text-foreground">
            {adjustedBaseline.toFixed(1)}%
          </span>

          <span className="text-muted-foreground">Booking pace</span>
          <span className="font-semibold text-foreground">
            {paceBand(paceRatio).label}
          </span>

          <span className="text-muted-foreground">Demand correction</span>
          <span
            className={cn(
              'font-semibold tabular-nums',
              demandCorrectionPct > 0
                ? 'text-emerald-600'
                : demandCorrectionPct < 0
                  ? 'text-rose-600'
                  : 'text-foreground'
            )}
          >
            {demandCorrectionPct >= 0 ? '+' : ''}{demandCorrectionPct.toFixed(1)}%
          </span>

          <span className="text-muted-foreground">Event adjustment</span>
          <span
            className={cn(
              'font-semibold tabular-nums',
              eventAdjustmentPct > 0 ? 'text-emerald-600' : 'text-foreground'
            )}
          >
            +{eventAdjustmentPct.toFixed(1)}%
          </span>
        </div>

        {/* Final rate footer */}
        <div className="border-t border-border/40 pt-2 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Final rate</span>
          <span className="font-bold tabular-nums text-foreground text-base">
            £{finalRate}
          </span>
        </div>
      </div>

      {/* Fixed parameters note */}
      <p className="text-[11px] text-muted-foreground">
        Fixed: Base rate £{BASE_RATE} · Normal baseline {NORMAL_BASELINE}% · Sensitivity: Balanced
      </p>
    </div>
  )
}
